/**
 * CarouselWidget
 *
 * Dashboard widget that displays a carousel of promotional content.
 * Self-contained - fetches its own data.
 */
import { gql, useQuery } from '@apollo/client'
import MuiCarousel from 'react-material-ui-carousel'
import { Box } from '@mui/material'
import { Circle } from '@mui/icons-material'

import { BigImageWithText, CardBox } from '../..'
import { useBrandingContext } from '../../../context/brandingProvider'

const CAROUSEL_QUERY = gql`
  query Carousel($instanceBrandingId: Int) {
    carousel(instanceBrandingId: $instanceBrandingId) {
      title
      body
      footer
      link
      imageUrl
    }
  }
`

function PlaceholderCarouselItem() {
  return (
    <BigImageWithText title="2023, What a year of giving!" footer="Read our Newsletter" link="https://givewise.ca" />
  )
}

export function CarouselWidget() {
  const branding = useBrandingContext()
  const instanceBrandingId = branding?.id ? parseInt(branding.id, 10) : null
  const { data, loading, error } = useQuery(CAROUSEL_QUERY, {
    variables: { instanceBrandingId },
  })

  const carousel = data?.carousel || []

  if (error) {
    return <PlaceholderCarouselItem />
  }

  if (loading || !carousel.length) return <PlaceholderCarouselItem />

  return (
    <Box sx={{ px: 3 }}>
      <CardBox
        sx={{
          p: 0,
          width: '100%',
        }}
      >
        <MuiCarousel
          navButtonsAlwaysInvisible
          autoPlay={carousel.length > 1}
          sx={{
            width: '100%',
            'button:hover svg': { fill: 'white' },
          }}
          interval={12000}
          duration={200}
          indicatorContainerProps={{
            style: {
              display: carousel.length < 2 ? 'none' : undefined,
              position: 'absolute',
              width: 'auto',
              bottom: '6px',
              right: '6px',
              zIndex: '1',
            },
          }}
          activeIndicatorIconButtonProps={{
            sx: {
              svg: { fill: '#fffa' },
            },
          }}
          indicatorIconButtonProps={{
            sx: {
              svg: { fill: '#3333', stroke: '#fff9' },
            },
          }}
          IndicatorIcon={<Circle sx={{ fontSize: '100%' }} />}
        >
          {carousel.map(({ title, body, footer, link, imageUrl }, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              sx={{ width: '100%', aspectRatio: '1208 / 636' }}
            >
              <BigImageWithText title={title} body={body} footer={footer} link={link} imageUrl={imageUrl} />
            </Box>
          ))}
        </MuiCarousel>
      </CardBox>
    </Box>
  )
}
